/**
 * Platform voice engines (Prompt 2.3): which native engine is live, health
 * per engine, calls in progress, and voice-harness scorecards (1.14).
 * Read-only — switching engines is an ops change (runbook).
 */
import { get } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';

export type VoiceEngineId = 'conversation_relay' | 'livekit' | 'vapi';
export type CheckStatus = 'ok' | 'degraded' | 'down' | 'unknown' | 'not_configured';

export interface HealthCheck {
  status: CheckStatus;
  detail: string;
  latencyMs: number | null;
}

export interface ScorecardGate {
  metric: string;
  value: number | null;
  threshold: number;
  comparator: 'lte' | 'gte' | 'eq';
  pass: boolean;
  kind: 'threshold' | 'regression';
  note?: string;
}

export interface ScorecardSummary {
  id: string;
  runId: string;
  engine: string;
  profileVersion: string | null;
  environment: string;
  trigger: string;
  gitSha: string | null;
  startedAt: string;
  finishedAt: string;
  callCount: number;
  passed: boolean;
  aggregates: Record<string, number | null>;
  gates: ScorecardGate[];
}

export interface EngineStatus {
  engine: VoiceEngineId;
  label: string;
  active: boolean;
  deployed: boolean;
  ready: boolean;
  health: CheckStatus;
  healthDetail: string;
  callsInProgress: number;
  latestScorecard: ScorecardSummary | null;
}

export interface VoiceEnginesStatus {
  generatedAt: string;
  environment: string;
  activeEngine: VoiceEngineId;
  overridesEnabled: boolean;
  engines: EngineStatus[];
  health: {
    gateway: HealthCheck & {
      url: string | null;
      activeCalls: number | null;
      engines: Record<string, number> | null;
    };
    livekitWorker: HealthCheck & {
      workers: Array<{
        instance: string;
        registered: boolean;
        activeJobs: number;
        draining: boolean;
        reportedAt: string;
      }>;
    };
    twilio: HealthCheck;
  };
  callsInProgress: { total: number; byEngine: Record<string, number> };
  runbook: { path: string; anchor: string };
}

export const voiceEnginesService = {
  getStatus(): Promise<VoiceEnginesStatus> {
    return get<VoiceEnginesStatus>(endpoints.platformVoice.engines);
  },
  listScorecards(params: { limit?: number; environment?: string } = {}): Promise<ScorecardSummary[]> {
    return get<ScorecardSummary[]>(endpoints.platformVoice.scorecards, { params });
  },
} as const;
